import { useState } from "react";
import styled from "styled-components";

import { colors } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";

import Modal from "components/molecules/Modal";
import Portal from "components/organisms/Portal";
import { ModalBody, ErrorText, ButtonRow } from "pages/Sikring/components/SikringComponents";
import { fmtDate, formatNumber, fmtPriceWithUnit, priceOf } from "pages/Sikring/sikringUtils";
import { Sikring } from "pages/Sikring/types";

interface ChecklistItem {
	name: string;
	value: string;
	/** Verdien mangler på sikringen. Punktet kan da ikke krysses av, siden det ikke finnes noe å kontrollere */
	missing: boolean;
}

function checkKey(item: ChecklistItem): string {
	return `${item.name}:${item.value}`;
}

interface QualityChecklistModalProps {
	sikring: Sikring;
	volumeKwh: number | null;
	pending: boolean;
	errorMessage: string | null;
	onConfirm: () => void;
	onClose: () => void;
}

/** Sjekkliste som må krysses av punkt for punkt før sikringsordren kan markeres som kvalitetssikret. */
export function QualityChecklistModal({ sikring, volumeKwh, pending, errorMessage, onConfirm, onClose }: Readonly<QualityChecklistModalProps>) {
	const price = priceOf(sikring);
	const items: ChecklistItem[] = [
		{ name: "Periode", value: `${fmtDate(sikring.start)} – ${fmtDate(sikring.slutt)}`, missing: false },
		{ name: "Volum", value: `${formatNumber(volumeKwh)} kWh`, missing: volumeKwh === null },
		{ name: "Kundepris (eks. mva)", value: fmtPriceWithUnit(price), missing: price === null },
		{ name: "Kjøpspris (eks. mva)", value: fmtPriceWithUnit(sikring.innpris), missing: sikring.innpris === null },
		{ name: "Margin (eks. mva)", value: fmtPriceWithUnit(sikring.fortjeneste), missing: sikring.fortjeneste === null },
	];
	// Avkrysningen gjelder verdien som ble kontrollert. Endres verdien mens modalen er åpen (listen hentes jevnlig på nytt),
	// får punktet en ny nøkkel og må krysses av igjen.
	const [checked, setChecked] = useState<Set<string>>(new Set());
	const anyMissing = items.some((item) => item.missing);
	const allChecked = !anyMissing && items.every((item) => checked.has(checkKey(item)));

	function toggle(item: ChecklistItem, isChecked: boolean) {
		setChecked((prev) => {
			const next = new Set(prev);
			if (isChecked) next.add(checkKey(item));
			else next.delete(checkKey(item));
			return next;
		});
	}

	return (
		<Portal>
			<Modal onClose={onClose}>
				<ModalBody role="dialog" aria-modal="true" aria-labelledby="kvalitetssikring-tittel">
					<ModalTitle id="kvalitetssikring-tittel">Kvalitetssikre sikringsordre</ModalTitle>
					<ModalIntro>Kontroller hvert punkt mot avtalen med kunden, og kryss av når det stemmer.</ModalIntro>
					<ChecklistList>
						{items.map((item) => (
							<li key={item.name}>
								<ChecklistLabel>
									<input
										type="checkbox"
										checked={checked.has(checkKey(item))}
										disabled={pending || item.missing}
										onChange={(e) => toggle(item, e.target.checked)}
									/>
									<span>{item.name}</span>
									<ChecklistValue>{item.value}</ChecklistValue>
								</ChecklistLabel>
							</li>
						))}
					</ChecklistList>
					{anyMissing && <ErrorText>Sikringsordren mangler verdier, og kan ikke kvalitetssikres før de er på plass.</ErrorText>}
					{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
					<ModalButtonRow>
						<Button type="button" label="Marker som kvalitetssikret" loading={pending} disabled={!allChecked || pending} onClick={onConfirm} />
						<Button type="button" secondary label="Avbryt" disabled={pending} onClick={onClose} />
					</ModalButtonRow>
				</ModalBody>
			</Modal>
		</Portal>
	);
}

const ModalTitle = styled.h2`
	margin: 0 0 0.5rem;
`;

const ModalIntro = styled.p`
	margin: 0 0 1rem;
`;

const ModalButtonRow = styled(ButtonRow)`
	margin-top: 1.5rem;
`;

const ChecklistList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const ChecklistLabel = styled.label`
	display: grid;
	grid-template-columns: auto 10rem 1fr;
	align-items: center;
	gap: 0.75rem;
	cursor: pointer;
	font-size: 0.875rem;
	font-weight: 700;
	color: ${colors.grey800};
`;

const ChecklistValue = styled.span`
	font-weight: 400;
`;
